import { ProjectService } from '@app/main/resourcesAllocation/projects/project.service';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppLocalizationService } from '@app/shared/common/localization/app-localization.service';
import { AlloCommonModule } from '@shared/common/common.module';
import { AppNavigationService } from '@app/shared/layout/nav/app-navigation.service';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { UtilsModule } from '@shared/utils/utils.module';
import {
  BsDatepickerModule,
  BsDatepickerConfig,
  BsDaterangepickerConfig,
} from 'ngx-bootstrap/datepicker';
import { ActivitiesAssignPanelComponent } from '@app/components/common/activities-drawer/activities-assign-panel/activities-assign-panel.component';
import { ActivitiesCreatePanelComponent } from '@app/components/common/activities-drawer/activities-create-panel/activities-create-panel.component';
import { ActivitiesDrawerComponent } from '@app/components/common/activities-drawer/activities-drawer.component';
import { AddWidgetModalComponent } from './customizable-dashboard/add-widget-modal/add-widget-modal.component';
import { AllocationActivitiesComponent } from '@app/components/allocation-sheets/allocation/allocation-activities/allocation-activities.component';
import { AllocationActivityItemComponent } from '@app/components/allocation-sheets/allocation/allocation-activities/allocation-activity-item/allocation-activity-item.component';
import { AllocationBulkDownloadModalComponent } from './allocation-bulk-download-modal/allocation-bulk-download-modal.component';
import { AllocationConditionsComponent } from '@app/components/allocation-sheets/allocation/allocation-conditions/allocation-conditions.component';
import { AllocationConditionsFormComponent } from '@app/components/allocation-sheets/allocation/allocation-conditions/allocation-conditions-form/allocation-conditions-form.component';
import { AllocationFiltersBarComponent } from '@app/components/allocation-sheets/allocation-filters-bar/allocation-filters-bar.component';
import { AppAuthService } from './auth/app-auth.service';

import { AppBsModalModule } from '@shared/common/appBsModal/app-bs-modal.module';
import { AppRouteGuard } from './auth/auth-route-guard';
import { AssignAssetsComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assets-assign/assets-assign.component';
import { AssignPersonnelComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-personnel/assign-personnel.component';
import { AssignPersonnelTableModelComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-personnel-table/assign-personnel-table.component';
import { AssignPlantsTableModelComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-plants-table/assign-plants-table.component';
import { AssignPlantsComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-plants/assign-plants.component';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CancelAllocationModalComponent } from '@app/components/allocation-sheets/allocation/cancel-modal/cancel-allocation-modal.component';
import { ChangeUserRolesModalComponent } from './change-user-roles-modal/change-user-roles-modal.component';
import { CheckboxInputTypeComponent } from './input-types/checkbox-input-type/checkbox-input-type.component';
import { CloneActivityModalComponent } from '@app/components/common/activities-drawer/activities-assign-panel/clone-activity-modal/clone-activity.modal.component';
import { ComboboxInputTypeComponent } from './input-types/combobox-input-type/combobox-input-type.component';
import { ComingSoonComponent } from '@app/components/coming-soon/coming-soon.component';
import { CommonLookupModalComponent } from './lookup/common-lookup-modal.component';
import { CopyAllocationDetailsConfirmModalComponent } from './copy-allocation-details-confirm-modal/copy-allocation-details-confirm-modal.component';
import { CountoModule } from 'angular2-counto';
import { CustomizableDashboardComponent } from './customizable-dashboard/customizable-dashboard.component';
import { DashboardViewConfigurationService } from './customizable-dashboard/dashboard-view-configuration.service';
import { DatePickerInitialValueSetterDirective } from './timing/date-picker-initial-value.directive';
import { DateRangePickerInitialValueSetterDirective } from './timing/date-range-picker-initial-value.directive';
import { DateTimeService } from './timing/date-time.service';
import { DelegateActivityModalComponent } from './delegate-activity-modal.component/delegate-activity-modal.component';
import { DelegateSignOffModalComponent } from './delegate-sign-off-modal/delegate-sign-off-modal.component';
import { DeleteStaffModalComponent } from './delete-staff-modal/delete-staff-modal.component';
import { EntityChangeDetailModalComponent } from './entityHistory/entity-change-detail-modal.component';
import { EntityTypeHistoryModalComponent } from './entityHistory/entity-type-history-modal.component';
import { FilterDateRangePickerComponent } from './customizable-dashboard/filters/filter-date-range-picker/filter-date-range-picker.component';
import { FiltersBarComponent } from '@app/components/activities/filters-bar/filters-bar.component';
import { PersonnelTableFilterComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-personnel-table/personnel-table-filter/personnel-table-filter.component';
import { PlantTableFilterComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-plants-table/plant-table-filter/plant-table-filter.component';
import { GridsterModule } from 'angular-gridster2';
import { HumanizeDatePipe } from '@shared/common/pipes/humanize-date';
import { HumanizeDateStringPipe } from '@shared/common/pipes/humanize-datestring';
import { RemoveStaffFromProjectConfirmModalComponent } from './remove-staff-from-project-confirm/remove-staff-from-project-confirm-modal.component';
import { RemoveCustomFormConfirmModalComponent } from './remove-custom-form-confirm/remove-custom-form-confirm-modal.component';

import { DayNamePipe } from '@shared/common/pipes/day-name';
import { DayStringNamePipe } from '@shared/common/pipes/day-string-name';

import { KeyValueListManagerComponent } from './key-value-list-manager/key-value-list-manager.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MultipleSelectComboboxInputTypeComponent } from './input-types/multiple-select-combobox-input-type/multiple-select-combobox-input-type.component';
import { NgxBootstrapDatePickerConfigService } from 'assets/ngx-bootstrap/ngx-bootstrap-datepicker-config.service';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NgxMatSelectSearchModule } from '@node_modules/ngx-mat-select-search';
import { NumbersOnlyDirective } from '@app/shared/numbers-only-directive';
import { OutstandingItemsModalComponent } from './outstanding-items-modal/outstanding-items-modal.component';
import { OverwriteAllocationDetailsConfirmModalComponent } from './overwrite-allocation-details-confirm-modal/overwrite-allocation-details-confirm-modal.component';
import { PaginatorModule } from 'primeng/paginator';
import { PasswordInputWithShowButtonComponent } from './password-input-with-show-button/password-input-with-show-button.component';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PopoverModule } from '@node_modules/ngx-bootstrap/popover';
import { RejectModalComponent } from '@app/components/allocation-sheets/allocation/reject-modal/reject.modal.component';
import { SheetStatusComponent } from '@app/components/common/sheet-status/sheet-status.component';
import { SingleLineStringInputTypeComponent } from './input-types/single-line-string-input-type/single-line-string-input-type.component';
import { StaffUserLookupTableModalComponent } from '@app/components/user-details/lookup-table-modal/staff-user-lookup-table-modal.component';
import { SubHeaderComponent } from '@app/shared/common/sub-header/sub-header.component';
import { TableModule } from 'primeng/table';
import { TimeZoneComboComponent } from './timing/timezone-combo.component';
import { TruncateModule } from '@yellowspot/ng-truncate';
import { UserDetailsComponent } from '@app/components/user-details/user-details.component';
import { UserDetailsManualComponent } from '@app/components/user-details/user-details-manual/user-details-manual.component';
import { UserRolesComponent } from '@app/components/user-roles/user-roles.component';
import { VarianceFromPlanComponent } from '@app/components/common/activities-drawer/activities-assign-panel/variance-from-plan/variance-from-plan.component';
import { WidgetDailySalesComponent } from './customizable-dashboard/widgets/widget-daily-sales/widget-daily-sales.component';
import { WidgetEditionStatisticsComponent } from './customizable-dashboard/widgets/widget-edition-statistics/widget-edition-statistics.component';
import { WidgetGeneralStatsComponent } from './customizable-dashboard/widgets/widget-general-stats/widget-general-stats.component';
import { WidgetHostTopStatsComponent } from './customizable-dashboard/widgets/widget-host-top-stats/widget-host-top-stats.component';
import { WidgetIncomeStatisticsComponent } from './customizable-dashboard/widgets/widget-income-statistics/widget-income-statistics.component';
import { WidgetMemberActivityComponent } from './customizable-dashboard/widgets/widget-member-activity/widget-member-activity.component';
import { WidgetProfitShareComponent } from './customizable-dashboard/widgets/widget-profit-share/widget-profit-share.component';
import { WidgetRecentTenantsComponent } from './customizable-dashboard/widgets/widget-recent-tenants/widget-recent-tenants.component';
import { WidgetRegionalStatsComponent } from './customizable-dashboard/widgets/widget-regional-stats/widget-regional-stats.component';
import { WidgetSalesSummaryComponent } from './customizable-dashboard/widgets/widget-sales-summary/widget-sales-summary.component';
import { WidgetSubscriptionExpiringTenantsComponent } from './customizable-dashboard/widgets/widget-subscription-expiring-tenants/widget-subscription-expiring-tenants.component';
import { WidgetTopStatsComponent } from './customizable-dashboard/widgets/widget-top-stats/widget-top-stats.component';
import { HistoryStatusModalComponent } from '@app/components/common/sheet-status/history-status-modal/history-status-modal.component';
import { StaffTableFilterComponent } from '@app/main/resourcesAllocation/staffs/staff-table-filter/staff-table-filter.component';
import { StatusDetailsComponent } from '@app/components/common/sheet-status/status-details/status-details.component';
import { ConnectionDrawerComponent } from '@app/admin/settings/allo-settings/connection-drawer/connection-drawer.component';
import { AssignMaterialsTableModelComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-materials-table/assign-materials-table.component';
import { MaterialTableFilterComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-materials-table/material-table-filter/material-table-filter.component';
import { AssignMaterialsComponent } from '@app/components/common/activities-drawer/activities-assign-panel/assign-materials/assign-materials.component';
import { ActivitiesFormsPanelComponent } from '@app/components/common/activities-drawer/activities-forms-panel/activities-forms-panel.component';
import { ActivitiesDynamicFormPanelComponent } from '@app/components/common/activities-drawer/activities-dynamic-form-panel/activities-dynamic-form-panel.component';
import { ControlsHostComponent } from './control-host/controls.host.component';
import { ControlSelectlistComponent } from './control-host/control-selectlist/control-selectlist.component';
import { FilterSupervisorSubmissionsByDayComponent } from './customizable-dashboard/filters/filter-supervisor-submissions-by-day/filter-supervisor-submissions-by-day.component';
import { WidgetSupervisorSubmissionsByDayComponent } from './customizable-dashboard/widgets/widget-supervisor-submissions-by-day/widget-supervisor-submissions-by-day.component';
import { WidgetPersonnelByDayComponent } from './customizable-dashboard/widgets/widget-personnel-by-day/widget-personnel-by-day.component';
import { WidgetDownloadSubmissionsComponent } from './customizable-dashboard/widgets/widget-download-submissions/widget-download-submissions.component';
import { WidgetPersonnelGraphComponent } from './customizable-dashboard/widgets/widget-personnel-graph/widget-personnel-graph.component';
import { WidgetPlantGraphComponent } from './customizable-dashboard/widgets/widget-plant-graph/widget-plant-graph.component';
import { WidgetWeeklyPayrollReportComponent } from './customizable-dashboard/widgets/widget-weekly-payroll-report/widget-weekly-payroll-report.component';
import { ActivitiesAddButtonComponent } from '@app/components/common/activities-add-button/activities-add-button.component';
import { MatMenuModule } from '@angular/material/menu';
import { DeleteAccountModalComponent } from './delete-accout-modal/delete-account-modal.component';
import { AllocationActivitiesReportComponent } from '@app/components/allocation-activities-report/allocation-activities-report.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ModalModule.forRoot(),
    UtilsModule,
    AlloCommonModule,
    TableModule,
    PaginatorModule,
    GridsterModule,
    TabsModule.forRoot(),
    BsDropdownModule.forRoot(),
    NgxChartsModule,
    BsDatepickerModule.forRoot(),
    PerfectScrollbarModule,
    CountoModule,
    AppBsModalModule,
    AutoCompleteModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatGridListModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    NgxMatSelectSearchModule,
    MatCheckboxModule,
    NgxMaterialTimepickerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    TruncateModule,
    PopoverModule,
    MatAutocompleteModule,
    MatInputModule,
    MatMenuModule
  ],
  declarations: [
    ActivitiesAssignPanelComponent,
    ActivitiesCreatePanelComponent,
    ActivitiesAddButtonComponent,
    ActivitiesDrawerComponent,
    ControlsHostComponent,
    ControlSelectlistComponent,
    ActivitiesFormsPanelComponent,
    ActivitiesDynamicFormPanelComponent,
    AddWidgetModalComponent,
    AllocationActivitiesComponent,
    AllocationActivityItemComponent,
    AllocationBulkDownloadModalComponent,
    AllocationConditionsComponent,
    AllocationConditionsFormComponent,
    AllocationFiltersBarComponent,
    PersonnelTableFilterComponent,
    StaffTableFilterComponent,
    PlantTableFilterComponent,
    AssignAssetsComponent,
    AssignPersonnelComponent,
    AssignPersonnelTableModelComponent,
    AssignPlantsTableModelComponent,
    AssignPlantsComponent,
    CancelAllocationModalComponent,
    ChangeUserRolesModalComponent,
    CheckboxInputTypeComponent,
    CloneActivityModalComponent,
    ComboboxInputTypeComponent,
    ComingSoonComponent,
    CommonLookupModalComponent,
    CopyAllocationDetailsConfirmModalComponent,
    CustomizableDashboardComponent,
    DatePickerInitialValueSetterDirective,
    DateRangePickerInitialValueSetterDirective,
    DelegateActivityModalComponent,
    DelegateSignOffModalComponent,
    DeleteAccountModalComponent,
    DeleteStaffModalComponent,
    RemoveStaffFromProjectConfirmModalComponent,
    RemoveCustomFormConfirmModalComponent,
    EntityChangeDetailModalComponent,
    EntityTypeHistoryModalComponent,
    FilterDateRangePickerComponent,
    FiltersBarComponent,
    HumanizeDatePipe,
    HumanizeDateStringPipe,
    DayNamePipe,
    DayStringNamePipe,
    KeyValueListManagerComponent,
    MultipleSelectComboboxInputTypeComponent,
    NumbersOnlyDirective,
    OutstandingItemsModalComponent,
    OverwriteAllocationDetailsConfirmModalComponent,
    PasswordInputWithShowButtonComponent,
    RejectModalComponent,
    SheetStatusComponent,
    SingleLineStringInputTypeComponent,
    StaffUserLookupTableModalComponent,
    SubHeaderComponent,
    TimeZoneComboComponent,
    UserDetailsComponent,
    UserDetailsManualComponent,
    UserRolesComponent,
    VarianceFromPlanComponent,
    WidgetDailySalesComponent,
    WidgetEditionStatisticsComponent,
    WidgetGeneralStatsComponent,
    WidgetHostTopStatsComponent,
    WidgetIncomeStatisticsComponent,
    WidgetMemberActivityComponent,
    WidgetProfitShareComponent,
    WidgetRecentTenantsComponent,
    WidgetRegionalStatsComponent,
    WidgetSalesSummaryComponent,
    WidgetSubscriptionExpiringTenantsComponent,
    WidgetTopStatsComponent,
    HistoryStatusModalComponent,
    StatusDetailsComponent,
    MaterialTableFilterComponent,
    AssignMaterialsComponent,
    FilterSupervisorSubmissionsByDayComponent,
    WidgetSupervisorSubmissionsByDayComponent,
    WidgetPersonnelByDayComponent,
    WidgetDownloadSubmissionsComponent,
    WidgetPersonnelGraphComponent,
    WidgetPlantGraphComponent,
    WidgetWeeklyPayrollReportComponent,
    AssignMaterialsTableModelComponent,
    AllocationActivitiesReportComponent,
    ConnectionDrawerComponent
  ],
  exports: [
    ActivitiesAssignPanelComponent,
    ActivitiesCreatePanelComponent,
    VarianceFromPlanComponent,
    ActivitiesAddButtonComponent,
    ActivitiesDrawerComponent,
    ActivitiesFormsPanelComponent,
    ActivitiesDynamicFormPanelComponent,
    AllocationActivitiesComponent,
    AllocationBulkDownloadModalComponent,
    AllocationConditionsComponent,
    AllocationFiltersBarComponent,
    AssignAssetsComponent,
    AssignPersonnelComponent,
    AssignPersonnelTableModelComponent,
    AssignPlantsTableModelComponent,
    AssignPlantsComponent,
    CancelAllocationModalComponent,
    ChangeUserRolesModalComponent,
    CloneActivityModalComponent,
    ComingSoonComponent,
    CommonLookupModalComponent,
    CopyAllocationDetailsConfirmModalComponent,
    CustomizableDashboardComponent,
    DatePickerInitialValueSetterDirective,
    DateRangePickerInitialValueSetterDirective,
    DelegateActivityModalComponent,
    DelegateSignOffModalComponent,
    DeleteAccountModalComponent,
    DeleteStaffModalComponent,
    RemoveStaffFromProjectConfirmModalComponent,
    RemoveCustomFormConfirmModalComponent,
    EntityChangeDetailModalComponent,
    EntityTypeHistoryModalComponent,
    FiltersBarComponent,
    PersonnelTableFilterComponent,
    StaffTableFilterComponent,
    PlantTableFilterComponent,
    HumanizeDatePipe,
    HumanizeDateStringPipe,
    DayNamePipe,
    DayStringNamePipe,
    KeyValueListManagerComponent,
    NgxChartsModule,
    OutstandingItemsModalComponent,
    OverwriteAllocationDetailsConfirmModalComponent,
    PasswordInputWithShowButtonComponent,
    RejectModalComponent,
    SheetStatusComponent,
    StaffUserLookupTableModalComponent,
    SubHeaderComponent,
    TimeZoneComboComponent,
    UserDetailsComponent,
    UserDetailsManualComponent,
    ControlsHostComponent,
    ControlSelectlistComponent,
    UserRolesComponent,
    AssignMaterialsTableModelComponent,
    MatIconModule,
    MatMenuModule,
    AllocationActivitiesReportComponent,
    ConnectionDrawerComponent
  ],
  providers: [
    AppLocalizationService,
    AppNavigationService,
    ProjectService,
    DashboardViewConfigurationService,
    DateTimeService,
    {
      provide: BsDatepickerConfig,
      useFactory: NgxBootstrapDatePickerConfigService.getDatepickerConfig,
    },
    {
      provide: BsDaterangepickerConfig,
      useFactory: NgxBootstrapDatePickerConfigService.getDaterangepickerConfig,
    },
  ],

  entryComponents: [
    CheckboxInputTypeComponent,
    ComboboxInputTypeComponent,
    FilterDateRangePickerComponent,
    MultipleSelectComboboxInputTypeComponent,
    SingleLineStringInputTypeComponent,
    WidgetDailySalesComponent,
    WidgetEditionStatisticsComponent,
    WidgetGeneralStatsComponent,
    WidgetHostTopStatsComponent,
    WidgetIncomeStatisticsComponent,
    WidgetMemberActivityComponent,
    WidgetProfitShareComponent,
    WidgetRecentTenantsComponent,
    WidgetRegionalStatsComponent,
    WidgetSalesSummaryComponent,
    WidgetSubscriptionExpiringTenantsComponent,
    WidgetTopStatsComponent,
    FilterSupervisorSubmissionsByDayComponent,
    WidgetSupervisorSubmissionsByDayComponent,
    WidgetPersonnelByDayComponent,
    WidgetWeeklyPayrollReportComponent,
    WidgetDownloadSubmissionsComponent,
    WidgetPersonnelGraphComponent,
    WidgetPlantGraphComponent
  ],
})
export class AppCommonModule {
  static forRoot(): ModuleWithProviders<AppCommonModule> {
    return {
      ngModule: AppCommonModule,
      providers: [AppAuthService, AppRouteGuard],
    };
  }
}
